import { useEffect, useState } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { fetchProfile } from '../services/authService';
import { User } from '../types/user';
import { useToast } from '../hooks/useToast';

export const ProfilePage = () => {
  const { user: authUser, token } = useAuth();
  const { showToast } = useToast();
  const [user, setUser] = useState<User | null>(authUser || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const profileData = await fetchProfile(token);
        setUser(profileData);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to load profile.', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [token, showToast]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <AppLayout title="Profile">
      <section className="card" style={{ padding: 28 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="subtle-text">Loading profile...</div>
          </div>
        ) : user ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0b4d99, #1fa2ff)',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '2rem',
                  flexShrink: 0,
                }}
              >
                {user.initials || 'U'}
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 700 }}>{user.name}</h2>
                <p style={{ margin: '8px 0 0', fontSize: '1rem', color: 'rgba(21, 37, 58, 0.7)' }}>{user.email}</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
              <div className="form-group">
                <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(21, 37, 58, 0.7)', marginBottom: 8 }}>
                  User ID
                </label>
                <div style={{ fontSize: '1rem', color: 'var(--neutral-900)' }}>#{user.id}</div>
              </div>

              <div className="form-group">
                <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(21, 37, 58, 0.7)', marginBottom: 8 }}>
                  Role
                </label>
                <div style={{ fontSize: '1rem', color: 'var(--neutral-900)' }}>
                  <span className="badge" style={{ textTransform: 'capitalize' }}>
                    {user.role || 'User'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(21, 37, 58, 0.7)', marginBottom: 8 }}>
                  Status
                </label>
                <div style={{ fontSize: '1rem', color: 'var(--neutral-900)' }}>
                  <span className={`badge ${user.is_active ? '' : 'badge-inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(21, 37, 58, 0.7)', marginBottom: 8 }}>
                  Last Login
                </label>
                <div style={{ fontSize: '1rem', color: 'var(--neutral-900)' }}>{formatDate(user.last_login_at)}</div>
              </div>

              {user.created_at && (
                <div className="form-group">
                  <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(21, 37, 58, 0.7)', marginBottom: 8 }}>
                    Account Created
                  </label>
                  <div style={{ fontSize: '1rem', color: 'var(--neutral-900)' }}>{formatDate(user.created_at)}</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="subtle-text">No profile data available.</div>
          </div>
        )}
      </section>
    </AppLayout>
  );
};

